import React, { useContext, useEffect, useRef, useState } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { Button, Modal, Form } from 'react-bootstrap';

import { ToastContext } from './context/ToastContext';
import { CustomerContext } from './context/CustomerContext';

function WalletPaymentMethodAddressModal({ billingAddress, userId, paymentMethodId, handleClose, show, fetchPaymentMethods, countries, isAbtraGiftCard }) {
    const [line1, setLine1] = useState('');
    const [line2, setLine2] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [country, setCountry] = useState('');
    const [loading, setLoading] = useState(false);
    const { sendMessage } = useContext(ToastContext);
    const submitButtonRef = useRef(null);
    const { customerData } = useContext(CustomerContext);
    const [selectedAddress, setSelectedAddress] = useState(null);

    const onAddressUpdate = async e => {
        if (!selectedAddress) {
            return;
        }
        e.preventDefault();
        try {
            setLoading(true);
            const url = new URL(`/api/user/${userId}/paymentMethods/${paymentMethodId}`, process.env.APP_URL);
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    mailingAddressId: selectedAddress.id,
                    ...typeof isAbtraGiftCard === 'boolean' && { isAbtraGiftCard },
                }),
            });
            const { error, success } = await response.json();
            if (error) {
                sendMessage(error, 'danger');
            }
            if (success) {
                sendMessage('Address updated successfully', 'success');
                handleClose();
                await fetchPaymentMethods();
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLine1(billingAddress.line1 || '');
        setLine2(billingAddress.line2 || '');
        setCity(billingAddress.city || '');
        setState(billingAddress.state || '');
        setPostalCode(billingAddress.postalCode || '');
        setCountry(countries.find(country => country.countryCode === billingAddress.country)?.title || '');
    }, [billingAddress]);

    useEffect(() => {
        if (!selectedAddress) {
            return;
        }
        setLine1(selectedAddress.address1 || '');
        setLine2(selectedAddress.address2 || '');
        setCity(selectedAddress.city || '');
        setState(selectedAddress.State?.title || '');
        setPostalCode(selectedAddress.zip || '');
        setCountry(selectedAddress.Country?.title || '');
    }, [selectedAddress]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            style={{ zIndex: 99999 }}
        >
            <Modal.Header
                closeButton
            >
                <Modal.Title>Update card address</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form
                    onSubmit={onAddressUpdate}
                >
                    <Form.Group
                        className={'mb-3'}
                        controlId={'walletPaymentMethod.Line1'}
                    >
                        <Form.Label>Line 1</Form.Label>
                        <Form.Control
                            type={'text'}
                            placeholder={'Enter line 1'}
                            value={line1}
                            disabled
                            required
                        />
                    </Form.Group>
                    <Form.Group
                        className={'mb-3'}
                        controlId={'walletPaymentMethod.Line2'}
                    >
                        <Form.Label>Line 2</Form.Label>
                        <Form.Control
                            type={'text'}
                            placeholder={'Enter line 2'}
                            value={line2}
                            disabled
                        />
                    </Form.Group>
                    <Form.Group
                        className={'mb-3'}
                        controlId={'walletPaymentMethod.City'}
                    >
                        <Form.Label>City</Form.Label>
                        <Form.Control
                            type={'text'}
                            placeholder={'Enter city'}
                            value={city}
                            disabled
                            required
                        />
                    </Form.Group>
                    <Form.Group
                        className={'mb-3'}
                        controlId={'walletPaymentMethod.State'}
                    >
                        <Form.Label>State</Form.Label>
                        <Form.Control
                            type={'text'}
                            placeholder={'Enter state'}
                            value={state}
                            disabled
                            required
                        />
                    </Form.Group>
                    <Form.Group
                        className={'mb-3'}
                        controlId={'walletPaymentMethod.PostalCode'}
                    >
                        <Form.Label>Postal Code</Form.Label>
                        <Form.Control
                            type={'text'}
                            placeholder={'Enter postal code'}
                            value={postalCode}
                            disabled
                            required
                        />
                    </Form.Group>
                    <Form.Group
                        className={'mb-3'}
                        controlId={'walletPaymentMethod.Country'}
                    >
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                            type={'text'}
                            placeholder={'Enter country'}
                            value={country}
                            disabled
                            required
                        />
                    </Form.Group>
                    <hr />
                    <h3
                        className={'mb-3'}
                    >
                        Select address
                    </h3>
                    <div
                        className={'wallet-payment-method-addresses'}
                    >
                        {customerData.MailingAddresses?.length ? customerData.MailingAddresses?.map(address => (
                            <div
                                key={address.id}
                                onClick={() => setSelectedAddress(address)}
                                className={`wallet-payment-method-address-box ${selectedAddress?.id === address.id ? 'selected' : ''}`}
                            >
                                <div>{address.address1}</div>
                                <div>{address.address2}</div>
                                <div>{address.city}</div>
                                <div>{address.State.title}</div>
                                <div>{address.Country.title}</div>
                                <div>{address.zip}</div>
                            </div>
                        )) : <p>Account has no addresses.</p>}
                    </div>
                    <Button
                        style={{ display: 'none' }}
                        variant={'primary'}
                        type={'submit'}
                        ref={submitButtonRef}
                    >
                        Submit
                    </Button>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant={'secondary'}
                    onClick={handleClose}
                    disabled={loading}
                >
                    Close
                </Button>
                <Button
                    variant={'primary'}
                    onClick={() => submitButtonRef.current.click()}
                    disabled={loading || !selectedAddress}
                >
                    Save Changes
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

WalletPaymentMethodAddressModal.propTypes = {
    billingAddress: object,
    userId: number,
    paymentMethodId: string,
    show: bool,
    handleClose: func,
    fetchPaymentMethods: func,
    countries: array,
    isAbtraGiftCard: bool,
};

export default WalletPaymentMethodAddressModal;